import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Download, Instagram } from "lucide-react";
import { useLang } from "@/context/LangContext";

const W = 1080;
const H = 1920;

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const drawSmiley = (ctx, cx, cy, r) => {
  ctx.save();
  ctx.fillStyle = "#D4FF00";
  ctx.strokeStyle = "#09090B";
  ctx.lineWidth = 10;
  ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
  ctx.fillStyle = "#09090B";
  ctx.beginPath(); ctx.arc(cx - r * 0.35, cy - r * 0.18, r * 0.11, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(cx + r * 0.35, cy - r * 0.18, r * 0.11, 0, Math.PI * 2); ctx.fill();
  ctx.strokeStyle = "#09090B"; ctx.lineWidth = r * 0.14; ctx.lineCap = "round";
  ctx.beginPath(); ctx.arc(cx, cy + r * 0.02, r * 0.5, 0.15 * Math.PI, 0.85 * Math.PI); ctx.stroke();
  ctx.restore();
};

const wrapText = (ctx, text, maxW) => {
  const words = text.split(" ");
  const lines = [];
  let line = "";
  words.forEach((w) => {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxW && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  });
  if (line) lines.push(line);
  return lines;
};

// Canvas-based Instagram story card for manifesto chapter 04 ("Mindset. Motivation. Community.").
const ManifestoStoryCard = ({ open, onClose, title, body }) => {
  const { t } = useLang();
  const canvasRef = useRef(null);
  const [url, setUrl] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const draw = async () => {
      try { await document.fonts.load('900 150px "Saira Condensed"'); await document.fonts.ready; } catch (e) {}
      if (cancelled) return;
      const c = canvasRef.current;
      if (!c) return;
      const ctx = c.getContext("2d");
      const cx = W / 2;

      // Background gradient
      const g = ctx.createLinearGradient(0, 0, W, H);
      g.addColorStop(0, "#FF3D8B"); g.addColorStop(0.45, "#C24BFF"); g.addColorStop(0.7, "#7B5CFF"); g.addColorStop(1, "#2FB8FF");
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);

      // deco blobs
      ctx.globalAlpha = 0.35;
      ctx.fillStyle = "#D4FF00"; ctx.beginPath(); ctx.arc(140, 260, 180, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#00F0FF"; ctx.beginPath(); ctx.arc(W - 110, H - 420, 160, 0, Math.PI * 2); ctx.fill();
      ctx.globalAlpha = 1;

      ctx.textAlign = "center";

      // Top pill
      ctx.fillStyle = "#09090B"; roundRect(ctx, cx - 300, 110, 600, 78, 39); ctx.fill();
      ctx.fillStyle = "#D4FF00"; ctx.font = '900 40px "Saira Condensed"';
      ctx.fillText("THE MANIFESTO · 04", cx, 165);

      // Title lines
      ctx.fillStyle = "#F9F8F6";
      ctx.font = '900 165px "Saira Condensed"';
      const titleLines = String(title).split(".").map((s) => s.trim()).filter(Boolean);
      titleLines.forEach((line, i) => ctx.fillText(line.toUpperCase() + ".", cx, 420 + i * 165));
      const afterTitleY = 420 + titleLines.length * 165 + 40;

      // Statement card
      const pw = 920, px = cx - pw / 2;
      ctx.font = '600 46px "Outfit"';
      const bodyLines = wrapText(ctx, body, pw - 120);
      const ph = bodyLines.length * 66 + 160;
      const py = afterTitleY;
      ctx.fillStyle = "#F9F8F6"; roundRect(ctx, px, py, pw, ph, 44); ctx.fill();
      ctx.lineWidth = 8; ctx.strokeStyle = "#09090B"; roundRect(ctx, px, py, pw, ph, 44); ctx.stroke();
      ctx.fillStyle = "#09090B"; ctx.textAlign = "left";
      bodyLines.forEach((line, i) => ctx.fillText(line, px + 60, py + 90 + i * 66));
      ctx.textAlign = "center";

      // Smiley overlapping card top-right
      drawSmiley(ctx, px + pw - 20, py - 20, 84);

      // Footer
      ctx.fillStyle = "#F9F8F6"; ctx.font = '900 46px "Saira Condensed"';
      ctx.fillText("FIGHT FOR YOURSELF", cx, py + ph + 130);
      ctx.fillStyle = "#D4FF00"; ctx.font = '900 38px "Saira Condensed"';
      ctx.fillText("ANTIUNHEALTHYCLUB.COM", cx, py + ph + 190);

      try { setUrl(c.toDataURL("image/png")); } catch (e) { setUrl(null); }
    };

    draw();
    return () => { cancelled = true; };
  }, [open, title, body]);

  const download = () => {
    const href = url || (canvasRef.current && canvasRef.current.toDataURL("image/png"));
    if (!href) return;
    const a = document.createElement("a");
    a.href = href; a.download = "manifesto-04.png"; a.click();
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div className="fixed inset-0 bg-ink/70 z-[80]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
          <motion.div
            className="fixed inset-0 z-[90] flex items-center justify-center p-4 pointer-events-none"
            initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.94 }}
          >
            <div className="pointer-events-auto border border-ink bg-cream w-full max-w-sm max-h-[92vh] overflow-y-auto p-5" data-testid="manifesto-story-modal">
              <div className="flex items-center justify-between mb-3">
                <p className="display uppercase text-xl">{t.manifestoShare}</p>
                <button onClick={onClose} data-testid="manifesto-story-close" className="btn bg-cream w-9 h-9 p-0 hover:bg-ink hover:text-cream">
                  <X className="w-4 h-4" strokeWidth={2} />
                </button>
              </div>
              <div className="border border-ink overflow-hidden bg-sand">
                <canvas ref={canvasRef} width={W} height={H} className="w-full h-auto block" data-testid="manifesto-story-canvas" />
              </div>
              <p className="text-xs text-center text-ink/60 mt-3 flex items-center justify-center gap-1.5">
                <Instagram className="w-3.5 h-3.5" strokeWidth={2} /> {t.builder.storyHint}
              </p>
              <button onClick={download} data-testid="manifesto-story-download" className="btn-primary w-full h-13 py-3.5 mt-3">
                <Download className="w-5 h-5" strokeWidth={2} /> {t.builder.download}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ManifestoStoryCard;
